import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Undo2 } from 'lucide-react';
import { AppUser, Listing } from '../../../types';
import { HISTORY_PAGE_SIZE, useListingHistory } from '../../../hooks/useListingHistory';
import { useSuperAdminActions } from '../../../hooks/useListingAdmin';
import {
  canPutBack,
  HistoryEntry,
  HistoryFieldChange,
  historyFieldLabel,
  summarizeValue,
} from '../../../utils/listingHistory';
import { formatDateTime, userLabel } from './superFormat';

const BUTTON =
  'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-3 rounded-[10px] text-sm font-medium border border-ink/15 text-ink transition-opacity disabled:opacity-40';

/** Values that are keys: shown with their label (e.g. status 'published' → "Published"). */
const VALUE_LABEL_PREFIX: Record<string, string> = {
  status: 'directory.listingStatus',
  statusBeforeDelete: 'directory.listingStatus',
  category: 'directory.category',
  subcategory: 'directory.sub',
  hoursMode: 'directory.hoursMode',
  nusach: 'nusach',
  'details.lendingType': 'directory.lendingType',
  'details.mikvahType': 'directory.sub',
};

/** Change history of one listing, newest first, with "Put back" (super admin only). */
export default function HistoryPanel({ listing, users }: { listing: Listing; users: AppUser[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [count, setCount] = useState(HISTORY_PAGE_SIZE);
  const { entries, loading, error, mayHaveMore } = useListingHistory(listing.id, count);
  const { putBack, saving } = useSuperAdminActions();
  const [result, setResult] = useState<{ id: string; ok: boolean } | null>(null);

  const who = (entry: HistoryEntry) => {
    if (entry.byScript) return t('directory.history.byScript');
    if (!entry.by) return t('directory.history.byUnknown');
    const user = users.find((u) => u.uid === entry.by);
    return t('directory.history.by', { who: user ? userLabel(user) : entry.by });
  };

  const handlePutBack = async (entry: HistoryEntry) => {
    if (!window.confirm(t('directory.history.confirmPutBack'))) return;
    setResult(null);
    try {
      await putBack(listing.id, entry);
      setResult({ id: entry.id, ok: true });
    } catch {
      setResult({ id: entry.id, ok: false });
    }
  };

  if (loading && entries.length === 0) return <p className="text-sm text-ash">{t('common.loading')}</p>;
  if (error) return <p role="alert" className="text-sm text-danger-600">{t('directory.history.loadFailed')}</p>;

  return (
    <div className="space-y-3">
      <p className="text-sm text-ash">{t('directory.history.intro')}</p>
      {entries.length === 0 && <p className="text-sm text-ash">{t('directory.history.none')}</p>}

      <ol className="space-y-3">
        {entries.map((entry) => (
          <li key={entry.id} className="rounded-[12px] bg-fog/60 p-3 space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p className="text-sm font-medium text-ink">
                {t(`directory.history.action.${entry.action}`)}
                <span className="text-ash font-normal">
                  {' · '}
                  {t('directory.history.fieldsCount', { count: entry.changes.length })}
                </span>
              </p>
              <p className="text-xs text-ash">
                <bdi>{formatDateTime(entry.at, isHebrew)}</bdi>
              </p>
            </div>
            <p className="text-xs text-ash break-all">
              <bdi>{who(entry)}</bdi>
            </p>

            <ul className="space-y-2">
              {entry.changes.map((change) => (
                <ChangeRow key={change.field} change={change} users={users} />
              ))}
            </ul>

            {canPutBack(entry) && (
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button type="button" disabled={saving} onClick={() => handlePutBack(entry)} className={BUTTON}>
                  <Undo2 className="w-4 h-4 rtl:-scale-x-100" aria-hidden />
                  {t('directory.history.putBack')}
                </button>
                {result?.id === entry.id && (
                  <p role="status" className={`text-sm ${result.ok ? 'text-ink' : 'text-danger-600'}`}>
                    {result.ok ? t('directory.history.putBackDone') : t('directory.admin.saveFailed')}
                  </p>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      {mayHaveMore && (
        <button type="button" onClick={() => setCount((c) => c + HISTORY_PAGE_SIZE)} className={BUTTON}>
          {t('directory.history.showMore')}
        </button>
      )}
    </div>
  );
}

function ChangeRow({ change, users }: { change: HistoryFieldChange; users: AppUser[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const label = historyFieldLabel(change.field);
  const name = label.raw
    ? label.raw
    : `${t(label.key)}${label.lang ? ` (${t(label.lang === 'en' ? 'directory.admin.english' : 'directory.admin.hebrew')})` : ''}`;

  const show = (value: unknown): string => {
    if (change.field === 'photoUrl' && value) return t('directory.history.changedData');
    if (change.field === 'createdAt' && typeof value === 'string') return formatDateTime(value, isHebrew) || value;
    if (change.field === 'ownerUids' && Array.isArray(value)) {
      if (value.length === 0) return t('directory.history.empty');
      return value
        .map((uid) => {
          const user = users.find((u) => u.uid === uid);
          return user ? user.email : String(uid);
        })
        .join(', ');
    }
    const prefix = VALUE_LABEL_PREFIX[change.field];
    if (prefix && typeof value === 'string' && value) return t(`${prefix}.${value}`, { defaultValue: value });
    const s = summarizeValue(value);
    switch (s.kind) {
      case 'empty':
        return t('directory.history.empty');
      case 'text':
        return s.text;
      case 'yesNo':
        return s.value ? t('directory.history.yes') : t('directory.history.no');
      case 'items':
        return t('directory.history.items', { count: s.count });
      default:
        return t('directory.history.changedData');
    }
  };

  const before = show(change.before);
  const after = show(change.after);
  const empty = t('directory.history.empty');
  if (!change.valuesOmitted && before === empty && after === empty) return null;
  // Same summary on both sides (e.g. "1 item" → "1 item"): say it changed.
  const afterText = before === after ? t('directory.history.changedData') : after;

  return (
    <li className="text-sm">
      <p className="font-medium text-ink">{name}</p>
      {change.valuesOmitted ? (
        <p className="text-xs text-ash">{t('directory.history.tooBig')}</p>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs">
          <dt className="text-ash">{t('directory.history.from')}:</dt>
          <dd className="text-ink/70 line-through decoration-ink/30 break-words whitespace-pre-wrap">
            <bdi>{before}</bdi>
          </dd>
          <dt className="text-ash">{t('directory.history.to')}:</dt>
          <dd className="text-ink break-words whitespace-pre-wrap">
            <bdi>{afterText}</bdi>
          </dd>
        </dl>
      )}
    </li>
  );
}
